import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { FeatureAccessService } from '@app/services/feature-access/feature-access.service';
import { UpgradeToEnterpriseDialogService } from '@app/services/feature-access/upgrade-to-enterprise-dialog.service';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { CheckoutSettings } from '@pdqdev/pdq-shared/dist/entities';
import { ExtensionComponentType } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { ExtComponentTypesOptions } from './config';
import { LayoutService } from '@app/storybook/services/layout/layout.service';
import { ShopService } from '@services/shops/shop.service';

@Component({
  selector: 'extension-header',
  templateUrl: './extension-header.component.html',
  styleUrls: ['./extension-header.component.scss'],
  standalone: false,
})
@UntilDestroy()
export class ExtensionHeaderComponent implements OnInit, OnChanges {
  title: string;
  @Input() saveDisabled: boolean;
  @Input() type: ExtensionComponentType;
  @Input() loadingExtension: boolean;
  @Input() showMetrics: boolean;
  @Output() onSave = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() onAddComponent = new EventEmitter<ExtensionComponentType>();
  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  checkoutSettings$ = this.store.select(fromCheckoutReducer.selectSettings);
  ComponentType: typeof ExtensionComponentType = ExtensionComponentType;
  // Recomputes the options once feature access loads.
  private featureAccessReady = toSignal(this.featureAccess.whenReady$.pipe(map(() => true)), {
    initialValue: false,
  });
  extComponentTypesOptions = computed(() => {
    this.featureAccessReady();
    const showMultiShipment = this.shopService.metadataSignal()?.showMultiShipmentExtension;
    return ExtComponentTypesOptions.filter((option) => this.isExtensionTypeVisible(option.value, showMultiShipment))
      .map((option) => this.withPlanLockState(option))
      .sort((a, b) => Number(a.locked) - Number(b.locked));
  });
  componentTypeOption: (typeof ExtComponentTypesOptions)[number] | null;
  checkoutSettings: CheckoutSettings;
  constructor(
    private store: Store<fromCheckoutReducer.State>,
    private router: Router,
    private layoutService: LayoutService,
    private shopService: ShopService,
    private featureAccess: FeatureAccessService,
    private upgradeToEnterpriseDialog: UpgradeToEnterpriseDialogService,
  ) {}

  ngOnInit(): void {
    this.initTitle();
    this.initComponentTypeOption();
    this.initCheckoutSettingsSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.type) {
      this.initTitle();
      this.initComponentTypeOption();
    }
  }
  private initCheckoutSettingsSubscription() {
    this.checkoutSettings$.pipe(untilDestroyed(this)).subscribe((settings) => {
      this.checkoutSettings = settings;
    });
  }

  private initComponentTypeOption() {
    this.componentTypeOption = ExtComponentTypesOptions.find((ex) => ex.value === this.type);
  }

  initTitle() {
    const title = ExtComponentTypesOptions.find((ex) => ex.value === this.type)?.label ?? 'Extensions';
    this.layoutService.customHeaderTitle$.set(title);
    this.layoutService.headerTitleCapitalized$.set(this.type !== ExtensionComponentType.ORDER_PROTECTION);
  }

  redirectToSettings() {
    this.router.navigate(['/settings/checkout']);
  }

  onLockedTypeClick() {
    this.upgradeToEnterpriseDialog.open();
  }

  onComponentTypeSelect(type: ExtensionComponentType) {
    this.store.dispatch(CheckoutActions.resetActiveExtension());
    this.onAddComponent.emit(type);
  }

  private isExtensionTypeVisible(type: ExtensionComponentType, showMultiShipment: boolean): boolean {
    switch (type) {
      case ExtensionComponentType.DYNAMIC_EXTENSION:
        return false;
      case ExtensionComponentType.MULTI_SHIPMENT:
        return !!showMultiShipment;
      default:
        return true;
    }
  }

  private withPlanLockState<T extends { value: ExtensionComponentType }>(option: T): T & { locked: boolean } {
    return { ...option, locked: !this.featureAccess.isExtensionTypeEnabled(option.value) };
  }
}
